import { Controller } from "@hotwired/stimulus"
import { createControlChannelSubscription } from "../channels/control_channel"

/**
 * Stimulus controller for moderator interface real-time updates.
 * Handles ControlChannel subscription for moderator stats and navigation events.
 *
 * Uses the singleton ActionCable consumer via createControlChannelSubscription helper,
 * ensuring proper lifecycle management (automatic cleanup on disconnect).
 *
 * @class ModeratorInterfaceController
 * @extends Controller
 */
export default class extends Controller {
  static values = {
    surveyId: Number
  }

  /**
   * Called when the controller is connected to the DOM.
   * Sets up the ControlChannel subscription for real-time updates.
   */
  connect() {
    this.currentPosition = this.getCurrentPosition()
    this.setupControlChannel()
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Cleans up the ControlChannel subscription.
   */
  disconnect() {
    if (this.controlSub) {
      try { this.controlSub.unsubscribe() } catch (_) {}
      this.controlSub = null
    }
  }

  /**
   * Set up the ControlChannel subscription with callbacks for moderator events.
   */
  setupControlChannel() {
    if (!this.surveyIdValue) {
      console.warn("ModeratorInterface: No survey ID provided")
      return
    }

    this.controlSub = createControlChannelSubscription(
      this.surveyIdValue,
      null, // userId not needed for moderator view
      {
        onSetNextQuestion: (position) => this.handleSetNextQuestion(position),
        onModeratorStats: (data) => this.updateStats(data),
        onAlert: (message, kind) => this.handleAlert(message, kind)
      }
    )
  }

  /**
   * Handle set_next_question events from other moderators.
   * Reloads the page if the question position changed.
   *
   * @param {number} position - The new question position
   */
  handleSetNextQuestion(position) {
    // Notify connection monitor of activity
    this.notifyConnectionActivity()

    if (this.currentPosition !== null && position !== this.currentPosition) {
      // Question position changed by another moderator - refresh to sync
      setTimeout(() => window.location.reload(), 300)
    }
  }

  /**
   * Handle alert events from other moderators.
   * Refreshes the page for go_to_question and end_survey events.
   *
   * @param {string} message - The alert message
   * @param {string} kind - The alert type (alert_only, alert_and_go_to_question, alert_and_end_survey)
   */
  handleAlert(message, kind) {
    // Notify connection monitor of activity
    this.notifyConnectionActivity()

    if (kind === "alert_and_go_to_question" || kind === "alert_and_end_survey") {
      // Navigation alert received - refresh to sync view
      setTimeout(() => window.location.reload(), 300)
    }
  }

  /**
   * Notify connection monitor that we received data from ActionCable.
   * This resets the staleness timer.
   */
  notifyConnectionActivity() {
    this.element.dispatchEvent(new CustomEvent('cable:received', { bubbles: true }))
  }

  /**
   * Update moderator interface stats from ControlChannel broadcast.
   * Uses jQuery for compatibility with existing DOM selectors.
   *
   * @param {Object} data - The moderator stats payload
   */
  updateStats(data) {
    // Notify connection monitor of activity
    this.notifyConnectionActivity()

    const $ = window.jQuery
    if (!$) return

    // Update global current question indicator
    $(`#global-current`).text(data.global_current)

    // Update user counts
    $(`.survey-${data.survey_id}-active-users`).text(data.user_total)
    $(`#survey-${data.survey_id}-quota-respondents`).text(`${data.respondent_total}/${data.max_respondents}`)

    // Update segment quotas
    if (data.segments) {
      data.segments.forEach((segment) => {
        $(`#segment-${segment.id}-quota`).text(`${segment.respondents}/${segment.quota}`)
      })
    }

    // Update progress bar if present
    const percentageBar = $(`#question-${data.question_id}-percentage-bar`)
    if (percentageBar.length > 0) {
      $(`#question-${data.question_id}-complete`).text(data.number)
      $(`.question-${data.question_id}-respondents`).text(data.respondent_total)

      const percentage = data.percentage
      $(`#question-${data.question_id}-percentage-text`).text(`${percentage}%`)
      percentageBar.css("width", `${percentage}%`)
      percentageBar.attr("aria-valuenow", percentage)

      if (percentage >= 75) {
        percentageBar.removeClass("bg-danger").addClass("bg-success")
      } else {
        percentageBar.addClass("bg-danger").removeClass("bg-success")
      }
    }

    // Keep local position tracker in sync
    if (data.global_current) {
      this.currentPosition = parseInt(data.global_current, 10) || this.currentPosition
    }
  }

  /**
   * Get the current question position from the DOM.
   *
   * @returns {number|null} The current position or null if not found
   */
  getCurrentPosition() {
    const el = document.getElementById("global-current")
    return el ? parseInt(el.textContent, 10) || null : null
  }
}
